/**
 * Settings → Storage: how much of the 5 MB per-person allowance your files use, and on what.
 * Everything you keep on Otable counts (figures, files, CVs, pictures); imports that are only read do not.
 * Backend: GET /users/me/storage/ (registry/user/storage.py).
 */
import React from 'react';
import useSWR from 'swr';
import { Alert, Box, LinearProgress, Paper, Skeleton, Typography } from '@mui/material';
import { fetcher } from 'shared/helpers/axios';

const LABELS = {
  figures: 'Figures in publications and proposals',
  collaboration_files: 'Collaboration files',
  reflections: 'Audio and video reflections',
  cv: 'CV on your profile',
  avatar: 'Profile picture',
  labs: 'Lab cover and news pictures',
  applications: 'CVs sent with applications',
  verification_documents: 'Membership verification documents',
};

export const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
};

export default function StorageSection() {
  const { data, error } = useSWR('/users/me/storage/', fetcher, { revalidateOnFocus: false });
  const pct = data ? Math.min(100, (data.used / data.limit) * 100) : 0;
  const parts = data ? Object.entries(data.parts).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]) : [];

  return (
    <Box>
      <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }}>Storage</Typography>
      <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2.5 }}>
        Every file you keep on Otable counts towards your storage: figures, shared files, CVs and pictures.
        Documents you import are read and not kept, so they don't count. Images are compressed when you upload them.
      </Typography>
      {error && <Alert severity="error">Your storage could not be loaded.</Alert>}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {!data ? <Skeleton height={60} /> : (
          <>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={1}>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                {formatBytes(data.used)} of {formatBytes(data.limit)} used
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{formatBytes(data.free)} free</Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct} aria-label="Storage used"
              color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary'} sx={{ height: 8, borderRadius: 4 }} />
            <Box sx={{ mt: 2.5 }}>
              {parts.length === 0 ? (
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>You don't keep any files on Otable yet.</Typography>
              ) : parts.map(([key, bytes]) => (
                <Box key={key} display="flex" justifyContent="space-between" sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: 14 }}>{LABELS[key] || key}</Typography>
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{formatBytes(bytes)}</Typography>
                </Box>
              ))}
            </Box>
            {pct >= 90 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You are close to your limit. Remove figures you took out of your texts, old files or an old CV to make room.
              </Alert>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
